import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { pricingCategories, contactInfo } from '../data/mock';

const buildBookingLink = (service, category) => {
  const msg = `Hi Dawn, I'd love to book: ${service.name} (${service.price}) - ${category.title} (${category.duration}). Please let me know your availability. Thank you!`;
  return `https://wa.me/${contactInfo.whatsappIntl}?text=${encodeURIComponent(msg)}`;
};

const PricingCard = ({ tier }) => (
  <motion.div
    key={tier.id}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.5 }}
    className="relative"
  >
    <div className="absolute inset-0 bg-white/10 rounded-3xl blur-xl" />
    <div className="relative bg-white/20 backdrop-blur-md rounded-3xl border border-white/30 p-8 md:p-12 shadow-xl">
      <div className="text-center mb-8">
        <p
          className="text-white/90 tracking-[0.3em] text-xs md:text-sm font-light mb-2"
          style={{ fontFamily: "'Josefin Sans', sans-serif" }}
        >
          {tier.duration} <span className="ml-1">{tier.emoji}</span>
        </p>
        <h3
          className="text-white font-light"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
          }}
        >
          {tier.title}
        </h3>
        <p
          className="text-white/80 tracking-[0.25em] text-xs mt-2"
          style={{ fontFamily: "'Josefin Sans', sans-serif" }}
        >
          {tier.subtitle}
        </p>
        <div className="mt-4 mx-auto w-16 h-px bg-white/40" />
      </div>

      <ul className="max-w-2xl mx-auto space-y-4">
        {tier.services.map((s, i) => (
          <li
            key={i}
            className="flex items-baseline justify-between gap-4 border-b border-white/20 pb-3 last:border-none group"
          >
            <span
              className="text-white font-medium text-base md:text-lg flex-1"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {s.name}
            </span>
            <span
              className="text-white font-light text-lg md:text-xl tracking-wide"
              style={{ fontFamily: "'Josefin Sans', sans-serif" }}
            >
              {s.price}
            </span>
            <a
              href={buildBookingLink(s, tier)}
              target="_blank"
              rel="noreferrer"
              className="text-xs tracking-[0.2em] text-white/80 hover:text-white border border-white/40 hover:bg-white hover:text-rose-500 rounded-full px-4 py-1.5 transition-all"
              style={{ fontFamily: "'Josefin Sans', sans-serif" }}
            >
              BOOK
            </a>
          </li>
        ))}
      </ul>
    </div>
  </motion.div>
);

const Pricing = () => {
  const [active, setActive] = useState(pricingCategories[0].id);
  const activeTier = pricingCategories.find((c) => c.id === active);

  return (
    <section id="price-list" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p
            className="text-white/80 tracking-[0.3em] text-xs md:text-sm font-light mb-4"
            style={{ fontFamily: "'Josefin Sans', sans-serif" }}
          >
            PRICE LIST
          </p>
          <h2
            className="text-white font-light tracking-[0.05em]"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
            }}
          >
            Kick Back &amp; Relax
          </h2>
          <div className="mt-6 mx-auto w-24 h-px bg-white/50" />
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10">
          {pricingCategories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`px-4 md:px-6 py-2.5 rounded-full text-xs tracking-[0.2em] font-light transition-all duration-500 border ${
                active === c.id
                  ? 'bg-white text-rose-500 border-white shadow-lg'
                  : 'bg-white/10 text-white border-white/40 hover:bg-white/25'
              }`}
              style={{ fontFamily: "'Josefin Sans', sans-serif" }}
            >
              {c.title.toUpperCase()}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <PricingCard key={activeTier.id} tier={activeTier} />
        </AnimatePresence>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center text-white/80 italic mt-10 text-sm md:text-base font-light"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          NB: There is an additional fee for after hour bookings
        </motion.p>
      </div>
    </section>
  );
};

export default Pricing;
